import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './Login.css';

function ForgotPassword() {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') || '';

    const handleSubmit = async () => {
        if (!email) {
            setErrorMessage(t('auth.emailRequired', 'Veuillez saisir votre adresse email.'));
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await fetch(`${API_BASE}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                throw new Error('Request failed');
            }

            setSuccessMessage(
                t('auth.resetEmailSent', 'Si un compte existe avec cet email, un lien de réinitialisation vous a été envoyé. Vérifiez votre boîte de réception.')
            );
            setEmail('');
        } catch (error) {
            console.error('Forgot password error:', error);
            setErrorMessage(t('auth.resetEmailFailed', 'Une erreur est survenue. Veuillez réessayer.'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    return (
        <div className="login-page">
            <div className="container py-4">
                <div className="login-card">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <Link to={`/${i18n.language}/login`} className="back-link">
                            <i className="bi bi-arrow-left me-1"></i>
                            {t('auth.backToLogin', 'Retour à la connexion')}
                        </Link>
                        <LanguageSwitcher variant="white-bg" />
                    </div>

                    {/* Icon */}
                    <div className="login-icon mb-3">
                        <i className="bi bi-key"></i>
                    </div>

                    <h2 className="text-center mb-2">{t('auth.forgotPasswordTitle', 'Mot de passe oublié')}</h2>
                    <p className="text-center text-muted mb-4">
                        {t('auth.forgotPasswordSubtitle', 'Saisissez votre email pour recevoir un lien de réinitialisation.')}
                    </p>

                    {/* Success Message */}
                    {successMessage && (
                        <div className="alert alert-success">
                            <i className="bi bi-check-circle me-2"></i>
                            {successMessage}
                        </div>
                    )}

                    {/* Error Message */}
                    {errorMessage && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            <i className="bi bi-exclamation-triangle me-2"></i>{errorMessage}
                            <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
                        </div>
                    )}

                    {/* Form */}
                    {!successMessage && (
                        <>
                            <div className="mb-4">
                                <label className="form-label">{t('common.email')}</label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-envelope"></i>
                                    </span>
                                    <input
                                        type="email"
                                        className="form-control"
                                        placeholder="exemple@email.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleSubmit}
                                className="btn btn-primary w-100 login-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        {t('common.loading')}
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-envelope-arrow-up me-2"></i>
                                        {t('auth.sendResetLink', 'Envoyer le lien')}
                                    </>
                                )}
                            </button>
                        </>
                    )}

                    <div className="divider my-4">
                        <span>{t('common.or')}</span>
                    </div>

                    <p className="text-center mb-0">
                        {t('auth.rememberPassword', 'Vous vous souvenez de votre mot de passe ?')}{' '}
                        <Link to={`/${i18n.language}/login`} className="register-link">
                            {t('auth.loginButton')}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default ForgotPassword;
